import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { ChargesPanel } from "@/components/ChargesPanel";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AttachmentStrip from "@/components/AttachmentStrip";
import RmsEvidence from "@/components/RmsEvidence";
import {
  FileText, Gavel, Plus, Search, X, ShieldCheck, CheckCircle2, Clock,
  Scale, ChevronDown, ChevronRight, FolderArchive, AlertTriangle, Printer, Users, Phone,
} from "lucide-react";
import { FrequentLocations } from "@/components/FrequentLocations";
import { TowBoard } from "@/components/TowBoard";
import { Truck, User, Car, Printer as PrinterIcon, Search as SearchIcon } from "lucide-react";
import { CallEventReport } from "@/components/rms/CallEventReport";
import { ConstableIntake } from "@/components/rms/ConstableIntake";
import { StopStats } from "@/components/rms/StopStats";
import { PersonProfileCard } from "@/components/PersonProfileCard";
import { VehicleProfileCard } from "@/components/VehicleProfileCard";

const REPORT_TYPES = ["Incident Report", "Offense Report", "Supplement", "Arrest Report",
  "Crash Report", "Traffic Citation", "Property Record", "Field Interview"];
const WARRANT_TYPES = [
  { v: "arrest", label: "Arrest Warrant" },
  { v: "search", label: "Search Warrant" },
  { v: "bench", label: "Bench Warrant" },
  { v: "fugitive", label: "Fugitive Warrant" },
];
const W_STATUS_COLOR = { active: "#DC2626", served: "#10B981", recalled: "#9CA3AF", expired: "#6B7280" };

const inputCls = "w-full bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-sm outline-none focus:border-[#0EA5E9] font-mono text-[#F3F4F6]";
const labelCls = "font-mono text-[10px] text-[#9CA3AF] uppercase mb-1 block";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-[#2C2D35] bg-[#141518] p-3 flex items-center gap-3" data-testid={`rms-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-9 h-9 flex items-center justify-center" style={{ background: color + "22", color }}><Icon size={18} /></div>
      <div>
        <div className="font-head font-bold text-xl leading-none" style={{ color }}>{value}</div>
        <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mt-1">{label}</div>
      </div>
    </div>
  );
}

function IndexSection({ icon: Icon, color, title, testid, children }) {
  const items = React.Children.toArray(children);
  return (
    <div data-testid={testid} className="border border-[#2C2D35] bg-[#141518]">
      <div className="px-3 py-2 border-b border-[#2C2D35] font-head text-xs font-bold flex items-center gap-2" style={{ color }}>
        <Icon size={14} /> {title}
      </div>
      <div className="p-3 space-y-1.5">
        {items.length === 0 ? <div className="font-mono text-[11px] text-[#6B7280]">No matches.</div> : items}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid={testid}>
      <div className="w-full max-w-2xl bg-[#141518] border border-[#2C2D35] my-8">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2C2D35]">
          <div className="font-head font-bold text-sm">{title}</div>
          <button data-testid="modal-close" onClick={onClose} className="text-[#9CA3AF] hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export default function RMS() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("records");
  const [records, setRecords] = useState([]);
  const [warrants, setWarrants] = useState([]);
  const [recFilter, setRecFilter] = useState({ q: "", type: "", status: "" });
  const [wFilter, setWFilter] = useState({ q: "", type: "", status: "" });
  const [showRecModal, setShowRecModal] = useState(false);
  const [showWntModal, setShowWntModal] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [nameQ, setNameQ] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profilePlate, setProfilePlate] = useState("");
  const [nameRes, setNameRes] = useState(null);
  const [nameBusy, setNameBusy] = useState(false);

  const canManage = ["records_supervisor", "supervisor", "watch_commander", "admin"].includes(user?.role);
  const canApprove = canManage;

  const loadRecords = useCallback(async () => { try { const { data } = await client.get("/rms"); setRecords(data); } catch { /* noop */ } }, []);
  const loadWarrants = useCallback(async () => {
    if (!canManage) return;
    try {
      const params = {};
      if (wFilter.status) params.status = wFilter.status;
      if (wFilter.type) params.warrant_type = wFilter.type;
      if (wFilter.q) params.q = wFilter.q;
      const { data } = await client.get("/warrants", { params });
      setWarrants(data);
    } catch { /* noop */ }
  }, [wFilter.status, wFilter.type, wFilter.q, canManage]);
  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { loadWarrants(); }, [loadWarrants]);

  const approve = async (rnum) => {
    try { await client.post(`/rms/${rnum}/approve`); toast.success(`${rnum} approved`); loadRecords(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setWarrantStatus = async (w, status) => {
    let note = "";
    if (status === "served") note = window.prompt("Served-by / disposition note (optional):") || "";
    if (status === "recalled") note = window.prompt("Reason for recall:") || "";
    try { await client.patch(`/warrants/${w.id}/status`, { status, note }); toast.success(`Warrant ${status.toUpperCase()}`); loadWarrants(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const printWarrant = async (w) => {
    try {
      const r = await client.get(`/warrants/${w.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not generate warrant PDF"); }
  };
  const searchName = async () => {
    if (nameQ.trim().length < 2) { toast.error("Enter at least 2 characters"); return; }
    setNameBusy(true);
    try { const { data } = await client.get("/name-index", { params: { q: nameQ.trim() } }); setNameRes(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setNameBusy(false); }
  };
  const printProfile = () => {
    if (!nameRes) return;
    const esc = (v) => String(v ?? "—").replace(/</g, "&lt;");
    const section = (title, rows) => `<h3>${title} (${rows.length})</h3>` +
      (rows.length ? `<table>${rows.join("")}</table>` : `<div class="empty">No records.</div>`);
    const wRows = nameRes.warrants.map((w) => `<tr><td class="k">${esc((w.status || "").toUpperCase())}</td><td class="v"><b>${esc((w.warrant_type || "").toUpperCase())}</b> · ${esc(w.subject_name)}${w.charges ? " — " + esc(w.charges) : ""} <span class="muted">(${esc(w.warrant_number)}${w.bond_amount ? " · Bond " + esc(w.bond_amount) : ""})</span></td></tr>`);
    const rRows = nameRes.records.map((r) => `<tr><td class="k">${esc(r.report_number)}</td><td class="v">${esc(r.report_type)} · ${esc(r.title)} <span class="muted">[${esc(r.approval_status)}]</span></td></tr>`);
    const fRows = nameRes.forms.map((f) => `<tr><td class="k">${esc(f.report_number)}</td><td class="v">${esc(f.label || f.form_type)} · ${esc((f.data && (f.data.subject_name || f.data.property_name)) || "—")}${f.incident_cc ? " · CC " + esc(f.incident_cc) : ""}</td></tr>`);
    const iRows = nameRes.incidents.map((i) => `<tr><td class="k">CC ${esc(i.cc_number)}</td><td class="v">${esc(i.call_type_label)} · ${esc(i.location?.address)} <span class="muted">[${esc(i.status)}]</span></td></tr>`);
    const win = window.open("", "_blank", "width=820,height=1000");
    if (!win) { toast.error("Pop-up blocked — allow pop-ups to print the profile sheet"); return; }
    win.document.write(`<html><head><title>Person Profile — ${esc(nameRes.query)}</title><style>
      body{font-family:Georgia,serif;color:#111;padding:40px;max-width:760px;margin:auto}
      h1{font-size:20px;border-bottom:3px solid #111;padding-bottom:8px;margin-bottom:4px}
      h3{font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#444;margin:22px 0 6px}
      table{width:100%;border-collapse:collapse;font-size:13px}
      td{padding:5px 8px;border-bottom:1px solid #ddd;vertical-align:top}
      td.k{width:26%;color:#555;font-weight:bold;white-space:nowrap}
      .muted{color:#888;font-size:11px}.empty{font-size:12px;color:#999;padding:4px 0}
      .meta{font-size:11px;color:#666;margin-bottom:6px}
      .foot{margin-top:30px;font-size:10px;color:#888;border-top:1px solid #ccc;padding-top:8px}
    </style></head><body>
      <h1>Consolidated Person Profile</h1>
      <div class="meta">Subject query: <b>${esc(nameRes.query)}</b> · ${nameRes.total} associated record(s)<br/>
        Matcom Office Of Public Safety · Prepared by ${esc(user?.name)} · ${new Date().toLocaleString()}</div>
      ${section("Active / Historical Warrants", wRows)}
      ${section("RMS Records", rRows)}
      ${section("Field Forms", fRows)}
      ${section("Calls-for-Service", iRows)}
      <div class="foot">CRIMINAL JUSTICE INFORMATION — FOR OFFICIAL USE ONLY. Confirm all warrant hits before enforcement. Generated by Matcom Integrated CAD.</div>
    </body></html>`);
    win.document.close(); win.focus(); setTimeout(() => win.print(), 350);
  };

  const filteredRecords = useMemo(() => records.filter((r) => {
    if (recFilter.type && r.report_type !== recFilter.type) return false;
    if (recFilter.status && r.approval_status !== recFilter.status) return false;
    if (recFilter.q) {
      const q = recFilter.q.toLowerCase();
      return [r.report_number, r.title, r.incident_number, r.created_by].some((f) => (f || "").toLowerCase().includes(q));
    }
    return true;
  }), [records, recFilter]);

  const stats = {
    total: records.length,
    pending: records.filter((r) => r.approval_status !== "approved").length,
    activeW: warrants.filter((w) => w.status === "active").length,
    servedW: warrants.filter((w) => w.status === "served").length,
  };

  return (
    <Layout>
      <PageHeader title="Records Management System" sub="Matcom Records Division · Master records · warrants · evidence · approval workflow" seal="/brand/records-seal.png" />
      <div className="flex-1 overflow-y-auto p-1">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard icon={FileText} label="Total Records" value={stats.total} color="#0EA5E9" />
          <StatCard icon={Clock} label="Pending Approval" value={stats.pending} color="#F59E0B" />
          <StatCard icon={Gavel} label="Active Warrants" value={stats.activeW} color="#DC2626" />
          <StatCard icon={CheckCircle2} label="Served Warrants" value={stats.servedW} color="#10B981" />
        </div>

        <div className="mb-4"><FrequentLocations /></div>

        <div className="flex items-center gap-1 border-b border-[#2C2D35] mb-3">
          <button data-testid="rms-tab-records" onClick={() => setTab("records")}
            className={`px-4 py-2 font-head text-sm border-b-2 flex items-center gap-2 ${tab === "records" ? "border-[#0EA5E9] text-[#0EA5E9]" : "border-transparent text-[#9CA3AF]"}`}>
            <FolderArchive size={15} /> Records
          </button>
          <button data-testid="rms-tab-incident-reports" onClick={() => nav("/rms-reports")}
            className="px-4 py-2 font-head text-sm border-b-2 border-transparent text-[#7dd3fc] flex items-center gap-2 hover:text-white">
            <FolderArchive size={15} /> Incident Reports
          </button>
          <button data-testid="rms-tab-daily-activity" onClick={() => nav("/daily-activity")}
            className="px-4 py-2 font-head text-sm border-b-2 border-transparent text-[#7dd3fc] flex items-center gap-2 hover:text-white">
            <FolderArchive size={15} /> Daily Activity
          </button>
          {canManage && (
            <button data-testid="rms-tab-warrants" onClick={() => setTab("warrants")}
              className={`px-4 py-2 font-head text-sm border-b-2 flex items-center gap-2 ${tab === "warrants" ? "border-[#DC2626] text-[#DC2626]" : "border-transparent text-[#9CA3AF]"}`}>
              <Gavel size={15} /> Warrants
            </button>
          )}
          {canManage && (
            <button data-testid="rms-tab-nameindex" onClick={() => setTab("nameindex")}
              className={`px-4 py-2 font-head text-sm border-b-2 flex items-center gap-2 ${tab === "nameindex" ? "border-[#8B5CF6] text-[#8B5CF6]" : "border-transparent text-[#9CA3AF]"}`}>
              <Users size={15} /> Name Index
            </button>
          )}
          <button data-testid="rms-tab-tows" onClick={() => setTab("tows")}
            className={`px-4 py-2 font-head text-sm border-b-2 flex items-center gap-2 ${tab === "tows" ? "border-[#F97316] text-[#F97316]" : "border-transparent text-[#9CA3AF]"}`}>
            <Truck size={15} /> Tow Board
          </button>
          <button data-testid="rms-tab-call-event" onClick={() => setTab("callevent")}
            className={`px-4 py-2 font-head text-sm border-b-2 flex items-center gap-2 ${tab === "callevent" ? "border-[#22D3EE] text-[#22D3EE]" : "border-transparent text-[#9CA3AF]"}`}>
            <PrinterIcon size={15} /> Call Event Report
          </button>
          {canManage && (
            <button data-testid="rms-tab-stop-stats" onClick={() => setTab("stopstats")}
              className={`px-4 py-2 font-head text-sm border-b-2 flex items-center gap-2 ${tab === "stopstats" ? "border-[#22D3EE] text-[#22D3EE]" : "border-transparent text-[#9CA3AF]"}`}>
              <Car size={15} /> Stop Stats
            </button>
          )}
          <button data-testid="rms-tab-constable-intake" onClick={() => setTab("constable")}
            className={`px-4 py-2 font-head text-sm border-b-2 flex items-center gap-2 ${tab === "constable" ? "border-[#B98A1E] text-[#B98A1E]" : "border-transparent text-[#9CA3AF]"}`}>
            <Gavel size={15} /> Constable Intake
          </button>
          <button data-testid="rms-tab-search-calls" onClick={() => nav("/search-calls")}
            className="px-4 py-2 font-head text-sm border-b-2 border-transparent text-[#7dd3fc] flex items-center gap-2 hover:text-white">
            <SearchIcon size={15} /> Search All Calls
          </button>
        </div>

        {tab === "tows" && <TowBoard />}
        {tab === "constable" && <ConstableIntake />}
        {tab === "stopstats" && canManage && <StopStats />}
        {tab === "callevent" && <CallEventReport isAdmin={user?.role === "admin"} canBatch={canManage} />}

        {tab === "records" && (
          <div data-testid="rms-records-panel">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-2.5 top-2.5 text-[#9CA3AF]" />
                <input data-testid="rms-search" value={recFilter.q} onChange={(e) => setRecFilter({ ...recFilter, q: e.target.value })}
                  placeholder="Search report #, title, Case#, author…" className={inputCls + " pl-8"} />
              </div>
              <select data-testid="rms-filter-type" value={recFilter.type} onChange={(e) => setRecFilter({ ...recFilter, type: e.target.value })} className={inputCls + " w-auto"}>
                <option value="">All types</option>
                {REPORT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select data-testid="rms-filter-status" value={recFilter.status} onChange={(e) => setRecFilter({ ...recFilter, status: e.target.value })} className={inputCls + " w-auto"}>
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
              </select>
              {canManage && (
                <button data-testid="rms-new-record" onClick={() => setShowRecModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#0EA5E9] text-black font-head font-semibold text-sm hover:bg-[#0284c7]">
                  <Plus size={15} /> New Record
                </button>
              )}
            </div>

            <table className="w-full text-left">
              <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase bg-[#0d0e11] sticky top-0">
                <tr><th className="px-3 py-2 w-6"></th><th className="px-3 py-2">Report #</th><th className="px-2">Type</th><th className="px-2">Title</th><th className="px-2">CC #</th><th className="px-2">Author</th><th className="px-2">Status</th><th className="px-2"></th></tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 && <tr><td colSpan={8} className="p-4 text-xs text-[#9CA3AF] font-mono">No records match. Create one with “New Record” or convert an incident from its detail page.</td></tr>}
                {filteredRecords.map((r) => {
                  const isOpen = expanded === r.report_number;
                  const own = r.created_by_user_id === user?.id;
                  return (
                    <React.Fragment key={r.report_number}>
                      <tr data-testid={`rms-row-${r.report_number}`} className="border-b border-[#2C2D35] hover:bg-[#141518] cursor-pointer" onClick={() => setExpanded(isOpen ? null : r.report_number)}>
                        <td className="px-3 py-2 text-[#9CA3AF]">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                        <td className="px-3 py-2 font-mono text-[11px]">{r.report_number}</td>
                        <td className="px-2 text-sm">{r.report_type}</td>
                        <td className="px-2 text-sm">{r.title}</td>
                        <td className="px-2 font-mono text-[11px] text-[#9CA3AF]">{r.incident_number || "—"}</td>
                        <td className="px-2 font-mono text-[11px]">{r.created_by_title || r.created_by}</td>
                        <td className="px-2">
                          <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 uppercase ${r.approval_status === "approved" ? "text-[#10B981]" : "text-[#F59E0B]"}`}
                            style={{ background: r.approval_status === "approved" ? "#10B98122" : "#F59E0B22" }}>{r.approval_status}</span>
                        </td>
                        <td className="px-2" onClick={(e) => e.stopPropagation()}>
                          {canApprove && r.approval_status !== "approved" && (
                            <button data-testid={`rms-approve-${r.report_number}`} onClick={() => approve(r.report_number)}
                              className="px-2 py-1 text-[11px] font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#10B981] hover:text-[#10B981] transition-colors">Approve</button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-[#2C2D35] bg-[#0d0e11]" onClick={(e) => e.stopPropagation()}>
                          <td colSpan={8} className="px-6 py-4">
                            {/* LAW-ENFORCEMENT-GRADE REPORT VIEW */}
                            <div className="border border-[#2C2D35] bg-[#101114]" data-testid={`rms-report-${r.report_number}`}>
                              <div className="px-4 py-2.5 flex items-center gap-3 border-b border-[#2C2D35]" style={{ background: "#12263A" }}>
                                <ShieldCheck size={16} className="text-[#E0B23C]" />
                                <div className="font-head font-bold text-sm text-white">MATCOM OFFICE OF PUBLIC SAFETY — {(r.report_type || "INCIDENT REPORT").toUpperCase()}</div>
                                <span className={`ml-auto font-mono text-[10px] font-bold px-2 py-0.5 ${r.approval_status === "approved" ? "text-[#10B981] border border-[#10B981]" : "text-[#F59E0B] border border-[#F59E0B]"}`}>
                                  {r.approval_status === "approved" ? "APPROVED" : "DRAFT — PENDING APPROVAL"}</span>
                                <button data-testid={`rms-pdf-${r.report_number}`} onClick={async () => {
                                  try {
                                    const resp = await client.get(`/rms/${r.report_number}/report.pdf`, { responseType: "blob" });
                                    const url = URL.createObjectURL(resp.data);
                                    const a = document.createElement("a");
                                    a.href = url; a.download = `${r.report_number}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                                  } catch { toast.error("PDF export failed"); }
                                }} className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-head font-bold text-black bg-[#E0B23C]">
                                  <PrinterIcon size={11} /> OFFICIAL PDF
                                </button>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 px-4 py-3 border-b border-[#2C2D35]">
                                {[["REPORT #", r.report_number], ["LINKED CC #", r.incident_number || "—"],
                                  ["REPORTING EMPLOYEE", r.created_by_title || r.created_by],
                                  ["CREATED", r.created_at ? new Date(r.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"],
                                  ["LOCATION", r.location || "—"], ["DISPOSITION", r.disposition_code || "—"],
                                  ["ATTACHMENTS", (r.attachments || []).length], ["STATUS", (r.status || "draft").toUpperCase()]].map(([k, v]) => (
                                  <div key={k}><div className="font-mono text-[9px] text-[#6B7280] uppercase">{k}</div><div className="text-xs text-white font-semibold truncate">{v}</div></div>
                                ))}
                              </div>
                              <div className="px-4 py-3 border-b border-[#2C2D35]"><ChargesPanel rmsNumber={r.report_number} compact /></div>
                              <div className="grid md:grid-cols-2 gap-3 px-4 py-3 border-b border-[#2C2D35]">
                                <div data-testid={`rms-persons-${r.report_number}`}>
                                  <div className="font-mono text-[9px] text-[#60A5FA] uppercase mb-1.5 font-bold">Involved Persons ({(r.persons || []).length})</div>
                                  {(r.persons || []).length === 0 && <div className="font-mono text-[10px] text-[#6B7280]">None recorded.</div>}
                                  {(r.persons || []).map((p, i) => (
                                    <div key={i} className="flex items-center gap-2 py-1 text-xs border-l-2 border-[#3B82F6] pl-2 mb-1 bg-[#0d0e11]">
                                      <User size={12} className="text-[#60A5FA] shrink-0" />
                                      <span className="text-white font-semibold">{p.name}</span>
                                      {p.dob && <span className="font-mono text-[10px] text-[#9CA3AF]">DOB {p.dob}</span>}
                                      {p.sex && <span className="font-mono text-[10px] text-[#9CA3AF]">{p.sex}</span>}
                                      <span className="ml-auto font-mono text-[9px] font-bold uppercase text-[#F59E0B]">{p.role || "linked"}</span>
                                    </div>
                                  ))}
                                </div>
                                <div data-testid={`rms-vehicles-${r.report_number}`}>
                                  <div className="font-mono text-[9px] text-[#4ade80] uppercase mb-1.5 font-bold">Involved Vehicles ({(r.vehicles || []).length})</div>
                                  {(r.vehicles || []).length === 0 && <div className="font-mono text-[10px] text-[#6B7280]">None recorded.</div>}
                                  {(r.vehicles || []).map((v, i) => (
                                    <div key={i} className="flex items-center gap-2 py-1 text-xs border-l-2 border-[#10B981] pl-2 mb-1 bg-[#0d0e11]">
                                      <Car size={12} className="text-[#4ade80] shrink-0" />
                                      <span className="text-white font-semibold">{[v.year, v.color, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}</span>
                                      {v.plate && <span className="font-mono text-[10px] text-[#9CA3AF]">{v.plate}{v.state ? `/${v.state}` : ""}</span>}
                                      <span className="ml-auto font-mono text-[9px] font-bold uppercase text-[#F59E0B]">{v.role || "linked"}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="px-4 py-3">
                                <div className="font-mono text-[9px] text-[#9CA3AF] uppercase mb-1 font-bold">Narrative</div>
                                <div className="text-sm text-[#D1D5DB] whitespace-pre-wrap leading-relaxed">{r.narrative || "No narrative on file."}</div>
                                <div className="mt-3">
                                  {r.form_id ? (
                                    <AttachmentStrip attachments={r.attachments} label="Evidence / Records" formId={r.form_id} canDelete={own || canManage} onChanged={loadRecords} />
                                  ) : (
                                    <RmsEvidence reportNumber={r.report_number} attachments={r.attachments || []} canManage={own || canManage} onChanged={loadRecords} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "warrants" && canManage && (
          <div data-testid="rms-warrants-panel">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-2.5 top-2.5 text-[#9CA3AF]" />
                <input data-testid="warrant-search" value={wFilter.q} onChange={(e) => setWFilter({ ...wFilter, q: e.target.value })}
                  placeholder="Search subject, warrant #, charges…" className={inputCls + " pl-8"} />
              </div>
              <select data-testid="warrant-filter-type" value={wFilter.type} onChange={(e) => setWFilter({ ...wFilter, type: e.target.value })} className={inputCls + " w-auto"}>
                <option value="">All types</option>
                {WARRANT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
              <select data-testid="warrant-filter-status" value={wFilter.status} onChange={(e) => setWFilter({ ...wFilter, status: e.target.value })} className={inputCls + " w-auto"}>
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="served">Served</option>
                <option value="recalled">Recalled</option>
                <option value="expired">Expired</option>
              </select>
              <button data-testid="rms-new-warrant" onClick={() => setShowWntModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#DC2626] text-white font-head font-semibold text-sm hover:bg-[#b91c1c]">
                <Plus size={15} /> New Warrant
              </button>
            </div>

            <div className="space-y-2">
              {warrants.length === 0 && <div className="p-4 text-xs text-[#9CA3AF] font-mono border border-[#2C2D35]">No warrants on file. Issue one with “New Warrant”.</div>}
              {warrants.map((w) => {
                const c = W_STATUS_COLOR[w.status] || "#9CA3AF";
                const isOpen = expanded === w.id;
                return (
                  <div key={w.id} data-testid={`warrant-row-${w.warrant_number}`} className="border border-[#2C2D35] bg-[#141518]">
                    <div className="p-3 flex items-start gap-3">
                      <Scale size={18} style={{ color: c }} className="mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 uppercase" style={{ background: c + "22", color: c }}>{w.status}</span>
                          <span className="font-mono text-[10px] font-bold text-[#0EA5E9]">{(w.warrant_type || "").toUpperCase()}</span>
                          {w.is_felony && <span className="font-mono text-[10px] font-bold text-white bg-[#7C2D12] px-1.5 py-0.5">FELONY</span>}
                          {w.extraditable && <span className="font-mono text-[10px] font-bold text-black bg-[#F59E0B] px-1.5 py-0.5">EXTRADITABLE</span>}
                          <span className="font-mono text-[10px] text-[#9CA3AF]">{w.warrant_number}</span>
                        </div>
                        <div className="font-head text-sm mt-1">{w.subject_name}{w.subject_dob ? ` · DOB ${w.subject_dob}` : ""}</div>
                        {w.charges && <div className="font-mono text-xs text-[#F87171] mt-0.5">{w.charges}</div>}
                        <div className="font-mono text-[11px] text-[#9CA3AF] mt-1">
                          {[w.issuing_court, w.judge && `Judge ${w.judge}`, w.bond_amount && `Bond ${w.bond_amount}`, w.linked_cc && `CC ${w.linked_cc}`].filter(Boolean).join(" · ") || "—"}
                        </div>
                        <button data-testid={`warrant-timeline-${w.warrant_number}`} onClick={() => setExpanded(isOpen ? null : w.id)} className="font-mono text-[10px] text-[#0EA5E9] mt-1 flex items-center gap-1">
                          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Timeline ({(w.timeline || []).length})
                        </button>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button data-testid={`warrant-pdf-${w.warrant_number}`} onClick={() => printWarrant(w)} className="px-2 py-1 text-[11px] font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#0EA5E9] hover:text-[#0EA5E9] flex items-center gap-1"><Printer size={12} /> PDF</button>
                        {w.status === "active" && (
                          <>
                            <button data-testid={`warrant-serve-${w.warrant_number}`} onClick={() => setWarrantStatus(w, "served")} className="px-2 py-1 text-[11px] font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#10B981] hover:text-[#10B981]">Serve</button>
                            <button data-testid={`warrant-recall-${w.warrant_number}`} onClick={() => setWarrantStatus(w, "recalled")} className="px-2 py-1 text-[11px] font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#9CA3AF]">Recall</button>
                            <button data-testid={`warrant-expire-${w.warrant_number}`} onClick={() => setWarrantStatus(w, "expired")} className="px-2 py-1 text-[11px] font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#6B7280]">Expire</button>
                          </>
                        )}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="border-t border-[#2C2D35] bg-[#0d0e11] p-3 space-y-1.5" data-testid={`warrant-timeline-body-${w.warrant_number}`}>
                        {w.narrative && <div className="text-xs text-[#D1D5DB] mb-2 whitespace-pre-wrap">{w.narrative}</div>}
                        {(w.timeline || []).map((t, i) => (
                          <div key={i} className="flex items-start gap-2 font-mono text-[11px]">
                            <span className="px-1.5 py-0.5 font-bold uppercase" style={{ background: (W_STATUS_COLOR[t.status] || "#9CA3AF") + "22", color: W_STATUS_COLOR[t.status] || "#9CA3AF" }}>{t.status}</span>
                            <span className="text-[#9CA3AF]">{new Date(t.ts).toLocaleString()} · {t.by}{t.note ? ` — ${t.note}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {tab === "nameindex" && canManage && (
          <div data-testid="rms-nameindex-panel">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search size={14} className="absolute left-2.5 top-2.5 text-[#9CA3AF]" />
                <input data-testid="nameindex-search" value={nameQ} onChange={(e) => setNameQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchName()}
                  placeholder="Search a person's name across records, warrants, forms & prior calls…" className={inputCls + " pl-8"} />
              </div>
              <button data-testid="nameindex-submit" onClick={searchName} disabled={nameBusy}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#8B5CF6] text-white font-head font-semibold text-sm hover:bg-[#7c3aed] disabled:opacity-50">
                <Search size={15} /> Search
              </button>
              <button data-testid="nameindex-profile" onClick={() => setProfileName(nameQ.trim())} disabled={nameQ.trim().length < 2}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#3B82F6] text-white font-head font-semibold text-sm hover:bg-[#2563eb] disabled:opacity-50">
                <User size={15} /> Person Profile Card
              </button>
              <PersonProfileCard name={profileName} open={!!profileName} onClose={() => setProfileName("")} />
              <button data-testid="nameindex-vehicle-profile" onClick={() => setProfilePlate(nameQ.trim())} disabled={nameQ.trim().length < 2}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#10B981] text-black font-head font-semibold text-sm hover:bg-[#059669] disabled:opacity-50">
                <Car size={15} /> Vehicle Profile Card
              </button>
              <VehicleProfileCard plate={profilePlate} open={!!profilePlate} onClose={() => setProfilePlate("")} onOpenPerson={(n) => { setProfilePlate(""); setProfileName(n); }} />
            </div>

            {!nameRes && <div className="p-4 text-xs text-[#9CA3AF] font-mono border border-[#2C2D35]">Enter a name to build a consolidated master-index view — active warrants, RMS records, field forms, and prior calls-for-service.</div>}
            {nameRes && (
              <div data-testid="nameindex-results" className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-mono text-[11px] text-[#9CA3AF]">Results for “<span className="text-[#8B5CF6]">{nameRes.query}</span>” — {nameRes.total} match(es)</div>
                  {nameRes.total > 0 && (
                    <button data-testid="nameindex-print-profile" onClick={printProfile}
                      className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-[#8B5CF6] text-white font-head font-semibold text-xs hover:bg-[#7c3aed]">
                      <Printer size={13} /> Print Profile Sheet
                    </button>
                  )}
                </div>

                <IndexSection icon={Gavel} color="#DC2626" title={`Warrants (${nameRes.warrants.length})`} testid="nameindex-warrants">
                  {nameRes.warrants.map((w, i) => (
                    <div key={i} className="font-mono text-xs text-[#D1D5DB]">
                      <span className="font-bold" style={{ color: W_STATUS_COLOR[w.status] || "#9CA3AF" }}>{(w.status || "").toUpperCase()}</span> · {(w.warrant_type || "").toUpperCase()} · {w.subject_name}{w.charges ? ` — ${w.charges}` : ""} <span className="text-[#9CA3AF]">({w.warrant_number})</span>
                    </div>
                  ))}
                </IndexSection>

                <IndexSection icon={FileText} color="#0EA5E9" title={`RMS Records (${nameRes.records.length})`} testid="nameindex-records">
                  {nameRes.records.map((r, i) => (
                    <div key={i} className="font-mono text-xs text-[#D1D5DB]">
                      <span className="text-[#9CA3AF]">{r.report_number}</span> · {r.report_type} · {r.title} <span className={r.approval_status === "approved" ? "text-[#10B981]" : "text-[#F59E0B]"}>[{r.approval_status}]</span>
                    </div>
                  ))}
                </IndexSection>

                <IndexSection icon={FileText} color="#D9A441" title={`Field Forms (${nameRes.forms.length})`} testid="nameindex-forms">
                  {nameRes.forms.map((f, i) => (
                    <div key={i} className="font-mono text-xs text-[#D1D5DB]">
                      {f.label || f.form_type} · {(f.data && (f.data.subject_name || f.data.property_name)) || "—"} <span className="text-[#9CA3AF]">({f.report_number}{f.incident_cc ? ` · CC ${f.incident_cc}` : ""})</span>
                    </div>
                  ))}
                </IndexSection>

                <IndexSection icon={Phone} color="#10B981" title={`Prior Calls-for-Service (${nameRes.incidents.length})`} testid="nameindex-incidents">
                  {nameRes.incidents.map((inc, i) => (
                    <div key={i} className="font-mono text-xs text-[#D1D5DB]">
                      <span className="text-[#9CA3AF]">{inc.public_ref ? `CAD# ${inc.public_ref} · ` : ""}CC {inc.cc_number}</span> · {inc.call_type_label} · {inc.location?.address || "—"} <span className="text-[#9CA3AF]">[{inc.status}]</span>
                      {inc.linked_as && <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-[#8B5CF6]/20 text-[#A78BFA]">linked as {inc.linked_as}{inc.linked_dob ? ` · DOB ${inc.linked_dob}` : ""}{inc.officer_initiated?.unit ? ` · ${inc.officer_initiated.unit}` : ""}</span>}
                    </div>
                  ))}
                </IndexSection>
              </div>
            )}
          </div>
        )}
      </div>

      {showRecModal && <NewRecordModal onClose={() => setShowRecModal(false)} onCreated={() => { setShowRecModal(false); loadRecords(); }} />}
      {showWntModal && <NewWarrantModal onClose={() => setShowWntModal(false)} onCreated={() => { setShowWntModal(false); loadWarrants(); }} />}
    </Layout>
  );
}

function NewRecordModal({ onClose, onCreated }) {
  const [f, setF] = useState({ report_type: "Incident Report", title: "", incident_number: "", location: "", unit: "", narrative: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try { await client.post("/rms", f); toast.success("Record created → pending approval"); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to create record"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New Record" onClose={onClose} testid="rms-record-modal">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Report Type</label>
          <select data-testid="record-type" value={f.report_type} onChange={(e) => setF({ ...f, report_type: e.target.value })} className={inputCls}>
            {REPORT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><label className={labelCls}>Related CC # (optional)</label>
          <input data-testid="record-cc" value={f.incident_number} onChange={(e) => setF({ ...f, incident_number: e.target.value })} className={inputCls} placeholder="2026-XXXXX" /></div>
        <div className="col-span-2"><label className={labelCls}>Title / Subject *</label>
          <input data-testid="record-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} /></div>
        <div><label className={labelCls}>Location</label>
          <input data-testid="record-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inputCls} /></div>
        <div><label className={labelCls}>Unit / Officer</label>
          <input data-testid="record-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className={inputCls} /></div>
        <div className="col-span-2"><label className={labelCls}>Narrative</label>
          <textarea data-testid="record-narrative" rows={4} value={f.narrative} onChange={(e) => setF({ ...f, narrative: e.target.value })} className={inputCls} /></div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-2 text-sm font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white">Cancel</button>
        <button data-testid="record-submit" onClick={submit} disabled={busy} className="px-4 py-2 text-sm font-head bg-[#0EA5E9] text-black font-semibold disabled:opacity-50">Create Record</button>
      </div>
    </Modal>
  );
}

function NewWarrantModal({ onClose, onCreated }) {
  const [f, setF] = useState({ warrant_type: "arrest", subject_name: "", subject_dob: "", subject_address: "",
    charges: "", issuing_court: "", judge: "", bond_amount: "", issued_date: "", expiry_date: "",
    is_felony: false, extraditable: false, narrative: "", linked_cc: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.subject_name.trim()) { toast.error("Subject name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, linked_cc: f.linked_cc.trim() || null };
      await client.post("/warrants", payload); toast.success("Warrant issued (ACTIVE)"); onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to issue warrant"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Issue New Warrant" onClose={onClose} testid="rms-warrant-modal">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Warrant Type</label>
          <select data-testid="warrant-type" value={f.warrant_type} onChange={(e) => setF({ ...f, warrant_type: e.target.value })} className={inputCls}>
            {WARRANT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select></div>
        <div><label className={labelCls}>Linked CC # (optional)</label>
          <input data-testid="warrant-cc" value={f.linked_cc} onChange={(e) => setF({ ...f, linked_cc: e.target.value })} className={inputCls} placeholder="2026-XXXXX" /></div>
        <div><label className={labelCls}>Subject Name *</label>
          <input data-testid="warrant-subject" value={f.subject_name} onChange={(e) => setF({ ...f, subject_name: e.target.value })} className={inputCls} /></div>
        <div><label className={labelCls}>Subject DOB</label>
          <input data-testid="warrant-dob" value={f.subject_dob} onChange={(e) => setF({ ...f, subject_dob: e.target.value })} className={inputCls} placeholder="MM/DD/YYYY" /></div>
        <div className="col-span-2"><label className={labelCls}>Subject Address</label>
          <input data-testid="warrant-address" value={f.subject_address} onChange={(e) => setF({ ...f, subject_address: e.target.value })} className={inputCls} /></div>
        <div className="col-span-2"><label className={labelCls}>Charges</label>
          <input data-testid="warrant-charges" value={f.charges} onChange={(e) => setF({ ...f, charges: e.target.value })} className={inputCls} placeholder="e.g. 18 Pa.C.S. § 3701 — Robbery" /></div>
        <div><label className={labelCls}>Issuing Court</label>
          <input data-testid="warrant-court" value={f.issuing_court} onChange={(e) => setF({ ...f, issuing_court: e.target.value })} className={inputCls} /></div>
        <div><label className={labelCls}>Judge</label>
          <input data-testid="warrant-judge" value={f.judge} onChange={(e) => setF({ ...f, judge: e.target.value })} className={inputCls} /></div>
        <div><label className={labelCls}>Bond Amount</label>
          <input data-testid="warrant-bond" value={f.bond_amount} onChange={(e) => setF({ ...f, bond_amount: e.target.value })} className={inputCls} placeholder="$50,000" /></div>
        <div><label className={labelCls}>Issued Date</label>
          <input data-testid="warrant-issued" value={f.issued_date} onChange={(e) => setF({ ...f, issued_date: e.target.value })} className={inputCls} placeholder="MM/DD/YYYY" /></div>
        <div className="col-span-2 flex items-center gap-6 pt-1">
          <label className="flex items-center gap-2 font-mono text-xs text-[#D1D5DB] cursor-pointer">
            <input data-testid="warrant-felony" type="checkbox" checked={f.is_felony} onChange={(e) => setF({ ...f, is_felony: e.target.checked })} /> Felony
          </label>
          <label className="flex items-center gap-2 font-mono text-xs text-[#D1D5DB] cursor-pointer">
            <input data-testid="warrant-extradite" type="checkbox" checked={f.extraditable} onChange={(e) => setF({ ...f, extraditable: e.target.checked })} /> Extraditable
          </label>
        </div>
        <div className="col-span-2"><label className={labelCls}>Narrative / PC Statement</label>
          <textarea data-testid="warrant-narrative" rows={3} value={f.narrative} onChange={(e) => setF({ ...f, narrative: e.target.value })} className={inputCls} /></div>
      </div>
      <div className="flex items-center gap-2 mt-3 border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2">
        <AlertTriangle size={14} className="text-[#F59E0B] shrink-0" />
        <div className="font-mono text-[10px] text-[#F59E0B]">Active warrants surface in NCIC person-name queries and MDT field lookups.</div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-2 text-sm font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white">Cancel</button>
        <button data-testid="warrant-submit" onClick={submit} disabled={busy} className="px-4 py-2 text-sm font-head bg-[#DC2626] text-white font-semibold disabled:opacity-50">Issue Warrant</button>
      </div>
    </Modal>
  );
}
